import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export const usePropertyTransfer = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);

    const generateClaimToken = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        const array = new Uint32Array(32);
        window.crypto.getRandomValues(array);
        let token = "";
        for (let i = 0; i < 32; i++) {
            token += chars.charAt(array[i] % chars.length);
        }
        return token;
    };

    const createTransfer = async (data) => {
        if (!user) return null;
        setLoading(true);

        try {
            const claimToken = generateClaimToken();

            const { error } = await supabase.from("property_transfers").insert({
                property_id: data.propertyId,
                sender_id: user.id,
                recipient_email: data.recipientEmail,
                claim_token: claimToken,
                selected_appliances: data.selectedAppliances,
                selected_paint_codes: data.selectedPaintCodes,
                // Fix: PropertyTransferSheet.jsx sends `selectedHomeLibrary` — this
                // used to read `data.selectedHouseVault` (a key that was never sent),
                // so selected documents silently never made it into the transfer.
                selected_house_vault: data.selectedHomeLibrary || [],
            });

            if (error) throw error;

            // Generate claim URL
            const claimUrl = `${window.location.origin}/claim-property?token=${claimToken}`;

            toast({
                title: "Transfer Created",
                description: "Share the link with the recipient to complete the transfer.",
            });

            return claimUrl;
        } catch (error) {
            console.error("Failed to create transfer:", error);
            toast({
                title: "Error",
                description: "Failed to create property transfer",
                variant: "destructive",
            });
            return null;
        } finally {
            setLoading(false);
        }
    };

    const claimTransfer = async (claimToken) => {
        if (!user) {
            toast({
                title: "Sign In Required",
                description: "Please sign in or create an account to claim this property.",
                variant: "destructive",
            });
            return null;
        }

        setLoading(true);

        try {
            const { data, error } = await supabase.rpc("claim_property_transfer", {
                p_claim_token: claimToken,
            });

            if (error) throw error;

            const result = data;

            if (!result.success) {
                toast({
                    title: "Error",
                    description: result.error || "Failed to claim property",
                    variant: "destructive",
                });
                return null;
            }

            // Show appropriate toast based on whether property is locked
            toast({
                title: result.is_locked ? "Property Claimed (Locked)" : "Property Claimed!",
                description: result.message || "The property and selected items have been transferred to your account.",
            });

            return result; // Return full result object with is_locked, message, etc.
        } catch (error) {
            console.error("Failed to claim transfer:", error);
            toast({
                title: "Error",
                description: "Failed to claim property transfer",
                variant: "destructive",
            });
            return null;
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        createTransfer,
        claimTransfer,
    };
};
